/* @generated */

// ../src/date_picker/date_picker.tsx
import * as React10 from "react";
import { ClickOutside } from "@dropbox/dig-components/click_outside";

// ../src/date_picker/constants.ts
var STRINGS = {
  previous: "Previous",
  next: "Next"
};
var NAVIGATION_KEYS = /* @__PURE__ */ new Set(["ArrowRight", "ArrowLeft", "ArrowUp", "ArrowDown"]);
var DATE_INPUT_FORMAT = "L/d/y";

// ../src/date_picker/context.tsx
import * as React from "react";
var defaultValue = {
  datePickerId: "",
  triggerRef: void 0,
  close: () => false,
  open: () => false,
  toggle: () => false,
  closeOnSelection: true,
  withPreviousLabel: STRINGS.previous,
  withNextLabel: STRINGS.next,
  focusedDate: null,
  isDateFocused: () => false,
  isDateSelected: () => false,
  isDateHovered: () => false,
  isDateBlocked: () => false,
  isFirstOrLastSelectedDate: () => false,
  onDateFocus: () => false,
  onDateHover: () => false,
  onDateSelect: () => false,
  goToPreviousMonths: () => false,
  goToNextMonths: () => false,
  isStartDate: () => false,
  isEndDate: () => false
};
var Context = React.createContext(defaultValue);

// ../src/date_picker/hooks/useDatePicker.ts
import * as React2 from "react";
import { useDatepicker as _useDatePicker } from "@datepicker-react/hooks";
function useDatePicker({
  picker,
  disabledDays,
  isDateBlocked,
  onSelection,
  onDateFocus
}) {
  const handleDatesChange = React2.useCallback(
    (data) => {
      picker.onDatesChange(data);
      if (onSelection) {
        const { startDate, focusedInput } = data;
        let { endDate } = data;
        const isSelectingStartDate = focusedInput === "endDate" /* EndDate */;
        if (isSelectingStartDate && !endDate) endDate = startDate;
        onSelection({
          startDate,
          endDate,
          selectedDate: isSelectingStartDate ? startDate : endDate
        });
      }
    },
    [picker, onSelection]
  );
  const {
    activeMonths,
    goToPreviousMonths,
    goToNextMonths,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked: _isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus: _onDateFocus,
    onDateSelect,
    onDateHover,
    goToDate,
    isStartDate,
    isEndDate
  } = _useDatePicker({
    startDate: picker.startDate,
    endDate: picker.endDate,
    // Ensures we can only pick 1 day if we're rendering a single picker
    minBookingDays: picker.type === 0 /* Single */ ? 1 : void 0,
    exactMinBookingDays: picker.type === 0 /* Single */ ? true : false,
    // If selecting an end date, we can't pick a date before the start date
    minBookingDate: picker.type === 1 /* Range */ && picker.focusedInput === "endDate" /* EndDate */ && picker.startDate || void 0,
    // If selecting a start date, we can't pick a date after the end date
    maxBookingDate: picker.type === 1 /* Range */ && picker.focusedInput === "startDate" /* StartDate */ && picker.endDate || void 0,
    unavailableDates: disabledDays,
    isDateBlocked,
    focusedInput: picker.focusedInput,
    onDatesChange: handleDatesChange,
    numberOfMonths: 1
  });
  const handleDateFocus = React2.useCallback(
    (date) => {
      if (_onDateFocus) _onDateFocus(date);
      if (onDateFocus) onDateFocus(date);
    },
    [onDateFocus, _onDateFocus]
  );
  const _onDateSelect = React2.useCallback(
    (date) => {
      if (picker.type === 1 /* Range */ && picker.focusedInput === "endDate" /* EndDate */ && picker.startDate === null && !_isDateBlocked(date)) {
        handleDatesChange({
          focusedInput: "endDate" /* EndDate */,
          startDate: null,
          endDate: date
        });
        return;
      }
      onDateSelect(date);
    },
    [
      _isDateBlocked,
      handleDatesChange,
      onDateSelect,
      picker.type,
      picker.focusedInput,
      picker.startDate
    ]
  );
  return {
    activeMonths,
    goToPreviousMonths,
    goToNextMonths,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked: _isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus: handleDateFocus,
    onDateSelect: _onDateSelect,
    onDateHover,
    goToDate,
    isStartDate,
    isEndDate
  };
}

// ../src/date_picker/hooks/useDatePickerSelector.ts
import * as React3 from "react";

// ../src/date_picker/utils.ts
import format from "date-fns/format";
import isDate from "date-fns/isDate";
import _isSameDay from "date-fns/isSameDay";
var getAnimationDirection = (placement) => {
  switch (placement) {
    case "top":
    case "top-start":
    case "top-end":
    default:
      return "up";
    case "bottom":
    case "bottom-start":
    case "bottom-end":
      return "down";
  }
};
var toDateString = (date, formatString) => {
  return format(date, formatString);
};
var isSameDay = (left, right) => {
  if (isDate(left) && isDate(right)) {
    return _isSameDay(left, right);
  }
  return left === right;
};
var getToday = () => {
  return /* @__PURE__ */ new Date();
};

// ../src/date_picker/hooks/useDatePickerSelector.ts
function useDatePickerSelector(selector) {
  const [state, setState] = React3.useState(selector.createInitialState());
  const setData = React3.useCallback(
    (data) => {
      const changes = selector.applyUpdates(data, state);
      if (changes) setState((prev) => ({ ...prev, ...changes }));
    },
    [state, selector]
  );
  const onDatesChange = React3.useCallback(
    ({ startDate, endDate, focusedInput }) => {
      setData({
        startDate,
        endDate,
        focusedInput: focusedInput || "startDate" /* StartDate */
      });
    },
    [setData]
  );
  const sync = React3.useCallback(
    ({ startDate, endDate }) => {
      setData({ startDate, endDate, focusedInput: selector.focusedInput });
    },
    [setData, selector.focusedInput]
  );
  return {
    type: selector.type,
    startDate: state.startDate,
    endDate: state.endDate,
    focusedInput: state.focusedInput,
    onDatesChange,
    sync
  };
}
var applyDatePickerUpdates = (data, state) => {
  if (data.focusedInput !== state.focusedInput) {
    return { focusedInput: data.focusedInput };
  }
  if (!isSameDay(state.startDate, data.startDate) || !isSameDay(state.endDate, data.endDate)) {
    return data;
  }
};
var createDatePickerSingleSelector = (selectedDate) => ({
  type: 0 /* Single */,
  focusedInput: "startDate" /* StartDate */,
  createInitialState: () => ({
    startDate: selectedDate,
    endDate: selectedDate,
    focusedInput: "startDate" /* StartDate */
  }),
  applyUpdates: applyDatePickerUpdates
});
var createDatePickerRangeSelector = (startDate, endDate, focusedInput) => ({
  type: 1 /* Range */,
  focusedInput,
  createInitialState: () => ({
    startDate,
    endDate,
    focusedInput: focusedInput || "startDate" /* StartDate */
  }),
  applyUpdates: applyDatePickerUpdates
});

// ../src/date_picker/hooks/useDay.ts
import * as React4 from "react";
import { useDay as _useDay } from "@datepicker-react/hooks";

// ../src/_global/constants.ts
var TAB_KEY = "Tab";
var ENTER_KEY = "Enter";
var ESC_KEY = "Escape";
var SPACEBAR_KEY = " ";
var buttonDataAttributeName = "data-dig-button";
var buttonDataSelector = `[${buttonDataAttributeName}]`;
var buttonContentDataAttributeName = "data-dig-button-content";
var buttonContentDataSelector = `[${buttonContentDataAttributeName}]`;
var splitButtonDataAttributeName = "data-dig-split-button";
var splitButtonDataSelector = `[${splitButtonDataAttributeName}]`;
var iconButtonDataAttributeName = "data-dig-icon-button";
var iconButtonDataSelector = `[${iconButtonDataAttributeName}]`;

// ../src/date_picker/hooks/useDay.ts
var useDay = ({ date }) => {
  const {
    close,
    triggerRef,
    closeOnSelection,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateSelect,
    onDateHover
  } = React4.useContext(Context);
  const [isSpacebarKey, setIsSpacebarKey] = React4.useState(false);
  const {
    onClick,
    onKeyDown,
    onMouseEnter,
    disabledDate,
    isSelected,
    isSelectedStartOrEnd,
    tabIndex
  } = _useDay({
    date,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus,
    onDateSelect,
    onDateHover
  });
  const handleKeyDown = React4.useCallback(
    (evt) => {
      if (isSpacebarKey && evt.key === TAB_KEY) {
        evt.preventDefault();
      } else if (evt.key === SPACEBAR_KEY) {
        setIsSpacebarKey(true);
      } else if (!isSpacebarKey) {
        if (onKeyDown) onKeyDown(evt);
      }
    },
    [onKeyDown, isSpacebarKey]
  );
  const handleKeyUp = React4.useCallback((evt) => {
    if (evt.key === SPACEBAR_KEY) {
      setIsSpacebarKey(false);
    }
  }, []);
  const isOutOfRange = React4.useCallback(
    (date2) => !isSelected && isDateBlocked(date2),
    [isDateBlocked, isSelected]
  );
  const handleClick = React4.useCallback(() => {
    if (date && isOutOfRange(date)) return;
    if (onClick) onClick();
    if (closeOnSelection) {
      close({ autoFocusRef: triggerRef });
    }
  }, [triggerRef, closeOnSelection, date, isOutOfRange, onClick, close]);
  const getDayProps = React4.useCallback(
    () => ({
      tabIndex,
      onClick: handleClick,
      onKeyDown: handleKeyDown,
      onKeyUp: handleKeyUp,
      onMouseEnter
    }),
    [onMouseEnter, tabIndex, handleClick, handleKeyDown, handleKeyUp]
  );
  return {
    getDayProps,
    isSelected,
    isSelectedStartOrEnd,
    isBlocked: isOutOfRange(date) || disabledDate,
    isFocused: isDateFocused(date)
  };
};

// ../src/date_picker/hooks/useMonth.ts
import * as React5 from "react";
import {
  useMonth as _useMonth
} from "@datepicker-react/hooks";
var monthLabelFormatFn = (date) => new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" }).format(date);
var weekdayLabelFormatFn = (date) => new Intl.DateTimeFormat("en-US", { weekday: "narrow" }).format(date);
var dayLabelFormatFn = (date) => new Intl.DateTimeFormat("en-US", { day: "numeric" }).format(date);
var useMonth = ({
  year,
  month,
  firstDayOfWeek,
  format: format2 = monthLabelFormatFn,
  weekdayLabelFormat = weekdayLabelFormatFn,
  dayLabelFormat = dayLabelFormatFn
}) => {
  const { days, weekdayLabels, monthLabel } = _useMonth({
    year,
    month,
    firstDayOfWeek,
    monthLabelFormat: format2,
    weekdayLabelFormat,
    dayLabelFormat
  });
  const { datePickerId, withPreviousLabel, withNextLabel, goToPreviousMonths, goToNextMonths } = React5.useContext(Context);
  const getPreviousMonthProps = React5.useCallback(
    () => ({ onClick: goToPreviousMonths }),
    [goToPreviousMonths]
  );
  const getNextMonthProps = React5.useCallback(() => ({ onClick: goToNextMonths }), [goToNextMonths]);
  return {
    datePickerId,
    withPreviousLabel,
    withNextLabel,
    days,
    weekdayLabels,
    monthLabel,
    getPreviousMonthProps,
    getNextMonthProps
  };
};

// ../src/date_picker/month.tsx
import * as React7 from "react";
import { ChevronLeftLine, ChevronRightLine } from "@dropbox/dig-icons/assets";
import { IconButton } from "@dropbox/dig-components/buttons";
import { IconButtonGroup } from "@dropbox/dig-components/combinations";
import { UIIcon } from "@dropbox/dig-icons";
import { Text } from "@dropbox/dig-components/typography";
import { Tooltip } from "@dropbox/dig-components/tooltips";

// ../src/date_picker/day.tsx
import * as React6 from "react";
import classNames from "classnames";
import isSameDay2 from "date-fns/isSameDay";

// ../src/date_picker/index.css.ts
var datePicker = "dig-10x86n21_21-7-0";
var datePickerBackDrop = "dig-10x86n20_21-7-0";
var datePickerCalendarGrid = "dig-10x86n27_21-7-0";
var datePickerDay = "dig-10x86n28_21-7-0";
var datePickerDayBlocked = "dig-10x86n2c_21-7-0";
var datePickerDayEnd = "dig-10x86n2e_21-7-0";
var datePickerDaySelected = "dig-10x86n2a_21-7-0";
var datePickerDaySelectedEdge = "dig-10x86n2b_21-7-0";
var datePickerDayStart = "dig-10x86n2d_21-7-0";
var datePickerDayToday = "dig-10x86n29_21-7-0";
var datePickerHeader = "dig-10x86n23_21-7-0";
var datePickerHiddenDropShadow = "dig-10x86n22_21-7-0";
var datePickerTitle = "dig-10x86n24_21-7-0";
var datePickerWeekDays = "dig-10x86n25_21-7-0";
var datePickerWeekday = "dig-10x86n26_21-7-0";

// ../src/date_picker/day.tsx
var Day = ({ date, children }) => {
  const domRef = React6.useRef(null);
  const { getDayProps, isFocused, isBlocked, isSelected, isSelectedStartOrEnd } = useDay({ date });
  const { isStartDate, isEndDate } = React6.useContext(Context);
  React6.useEffect(() => {
    if (domRef && domRef.current && isFocused && "focus" in domRef.current) {
      domRef.current.focus({ preventScroll: true });
    }
  }, [domRef, isFocused]);
  const isToday = isSameDay2(date, getToday());
  const _isStartDate = isStartDate(date);
  const _isEndDate = isEndDate(date);
  const cls = classNames(`dig-DatePicker-day ${datePickerDay}`, {
    // Since `@datepicker-react/hooks` uses `isWithinInterval` to check if the day is selected,
    // the <Day> component may not have `isSelected` set to `true` when it is `isSelectedStartOrEnd`.
    // For example, if the day prop of <Day> is `Thu Apr 25 2024 00:00:00` and selectedRange is [`Thu Apr 25 2024 15:56:48`, xxx],
    // then `isSelectedStartOrEnd` will be `true`, but `isSelected` will be `false`.
    // We expect the <Day> component that has the `dig-DatePicker-day--selectedEdge` classname
    // to also have the `dig-DatePicker-day--selected` classname
    "dig-DatePicker-day--selected": isSelected || isSelectedStartOrEnd,
    [`${datePickerDaySelected}`]: isSelected || isSelectedStartOrEnd,
    "dig-DatePicker-day--selectedEdge": isSelectedStartOrEnd,
    [`${datePickerDaySelectedEdge}`]: isSelectedStartOrEnd,
    "dig-DatePicker-day--blocked": isBlocked,
    [`${datePickerDayBlocked}`]: isBlocked,
    "dig-DatePicker-day--today": isToday,
    [`${datePickerDayToday}`]: isToday,
    "dig-DatePicker-day--start": _isStartDate,
    [`${datePickerDayStart}`]: _isStartDate,
    "dig-DatePicker-day--end": _isEndDate,
    [`${datePickerDayEnd}`]: _isEndDate
  });
  return /* @__PURE__ */ React6.createElement(
    "button",
    {
      ref: domRef,
      className: cls,
      type: "button",
      disabled: isBlocked,
      "aria-pressed": isSelected,
      ...getDayProps()
    },
    children
  );
};
Day.displayName = "Day";

// ../src/date_picker/month.tsx
var Month = ({
  year,
  month,
  firstDayOfWeek,
  format: format2,
  weekdayLabelFormat,
  dayLabelFormat
}) => {
  const {
    datePickerId,
    withPreviousLabel,
    withNextLabel,
    monthLabel,
    weekdayLabels,
    days,
    getPreviousMonthProps,
    getNextMonthProps
  } = useMonth({ year, month, firstDayOfWeek, format: format2, weekdayLabelFormat, dayLabelFormat });
  return /* @__PURE__ */ React7.createElement(React7.Fragment, null, /* @__PURE__ */ React7.createElement("div", { className: `dig-DatePicker-header ${datePickerHeader}` }, /* @__PURE__ */ React7.createElement(
    Text,
    {
      className: `dig-DatePicker-title ${datePickerTitle}`,
      size: "large",
      variant: "label",
      color: "standard"
    },
    monthLabel
  ), /* @__PURE__ */ React7.createElement(IconButtonGroup, { size: "small" }, ({ getButtonProps }) => /* @__PURE__ */ React7.createElement(React7.Fragment, null, /* @__PURE__ */ React7.createElement(Tooltip, { id: `${datePickerId}-previous`, title: withPreviousLabel }, /* @__PURE__ */ React7.createElement(
    IconButton,
    {
      variant: "transparent",
      type: "button",
      "data-testid": "digDatePickerPreviousMonth",
      ...getButtonProps(),
      ...getPreviousMonthProps()
    },
    /* @__PURE__ */ React7.createElement(UIIcon, { size: "small", src: ChevronLeftLine })
  )), /* @__PURE__ */ React7.createElement(Tooltip, { id: `${datePickerId}-next`, title: withNextLabel }, /* @__PURE__ */ React7.createElement(
    IconButton,
    {
      variant: "transparent",
      type: "button",
      "data-testid": "digDatePickerNextMonth",
      ...getButtonProps(),
      ...getNextMonthProps()
    },
    /* @__PURE__ */ React7.createElement(UIIcon, { size: "small", src: ChevronRightLine })
  ))))), /* @__PURE__ */ React7.createElement("div", { className: `dig-DatePicker-weekdays ${datePickerWeekDays}` }, weekdayLabels.map((weekdayLabel, idx) => /* @__PURE__ */ React7.createElement(
    Text,
    {
      key: idx,
      className: `dig-DatePicker-weekday ${datePickerWeekday}`,
      size: "xsmall",
      variant: "label",
      isBold: true
    },
    weekdayLabel
  ))), /* @__PURE__ */ React7.createElement("div", { className: `dig-DatePicker-calendar-grid ${datePickerCalendarGrid}` }, days.map(
    (day, idx) => typeof day === "object" ? /* @__PURE__ */ React7.createElement(Day, { key: day.date.toString(), date: day.date }, day.dayLabel) : /* @__PURE__ */ React7.createElement("div", { key: idx })
  )));
};
Month.displayName = "Month";

// ../src/date_picker/range_context.tsx
import * as React8 from "react";
var RangeContext = React8.createContext(void 0);

// ../src/date_picker/date_picker.tsx
import { useUniqueId } from "@dropbox/dig-components/hooks";
import {
  EASE_ENTER,
  EASE_LEAVE,
  useMotion
} from "@dropbox/dig-components/motion";
import { Overlay } from "@dropbox/dig-components/overlay";

// ../src/utils.ts
import * as React9 from "react";
var useDidUpdateEffect = (fn, deps) => {
  const didMountRef = React9.useRef(false);
  React9.useEffect(() => {
    if (didMountRef.current) fn();
    else didMountRef.current = true;
  }, deps);
};

// ../src/date_picker/date_picker.tsx
import classNames2 from "classnames";
var getDatePickerAnimation = (args) => {
  const { direction } = args;
  return /* @__PURE__ */ new Map([
    [
      "open",
      {
        keyFrames: {
          transform: direction === "up" ? `translate(0, -4px)` : `translate(0, 4px)`,
          opacity: 1
        },
        options: {
          duration: 0.125,
          ease: EASE_ENTER
        }
      }
    ],
    [
      "close",
      {
        keyFrames: {
          opacity: 0
        },
        options: {
          duration: 0.125,
          ease: EASE_LEAVE
        }
      }
    ]
  ]);
};
var DatePicker = ({
  id,
  selectedDate: _selectedDate,
  disabledDays = [],
  focusedInput = "startDate" /* StartDate */,
  isDateBlocked,
  onSelection,
  onDateFocus,
  withPreviousLabel = STRINGS.previous,
  withNextLabel = STRINGS.next,
  monthLabelFormat,
  weekdayLabelFormat,
  dayLabelFormat,
  auto = true,
  placement: placementProp = "bottom-start",
  positioningStrategy = "absolute",
  closeOnBlur = true,
  closeOnSelection = true,
  shouldPropagateClickOutsideMouseEvents = false,
  shouldHideDropShadow = false,
  isBlock = false,
  isPortaled = true,
  onToggle,
  firstDayOfWeek = 0,
  children
}) => {
  const selectedDate = React10.useMemo(() => {
    return _selectedDate === void 0 ? getToday() : _selectedDate;
  }, [_selectedDate]);
  const motionRef = React10.useRef(null);
  const triggerRef = React10.useRef(null);
  const overlayRef = React10.useRef(null);
  const [isOpen, setIsOpen] = React10.useState(false);
  const datePickerId = useUniqueId({ prefix: "dig-DatePicker-", idOverride: id });
  const rangeCtx = React10.useContext(RangeContext);
  const [placement, setPlacement] = React10.useState(placementProp);
  const [previousPlacementProp, setPreviousPlacementProp] = React10.useState(placementProp);
  if (previousPlacementProp !== placementProp) {
    setPlacement(placementProp);
    setPreviousPlacementProp(placementProp);
  }
  const onChangePlacement = (newPlacement) => {
    if (auto) {
      setPlacement(newPlacement);
    }
  };
  const [currentSelection, setCurrentSelection] = React10.useState(() => selectedDate);
  useDidUpdateEffect(() => {
    if (typeof onToggle === "function") {
      onToggle({ isOpen });
    }
  }, [isOpen]);
  React10.useEffect(() => {
    setCurrentSelection(selectedDate);
  }, [selectedDate]);
  const startDate = rangeCtx ? rangeCtx.selectedRange[0] : currentSelection;
  const endDate = rangeCtx ? rangeCtx.selectedRange[1] : currentSelection;
  const isRangePicker = rangeCtx ? true : false;
  const selector = React10.useMemo(
    () => isRangePicker ? createDatePickerRangeSelector(startDate, endDate, focusedInput) : createDatePickerSingleSelector(startDate),
    [isRangePicker, startDate, endDate, focusedInput]
  );
  const picker = useDatePickerSelector(selector);
  React10.useEffect(() => {
    picker.sync({ startDate, endDate });
  }, [startDate, endDate, picker]);
  const _onSelection = React10.useCallback(
    (options) => {
      if (onSelection) {
        onSelection(options);
      }
      setCurrentSelection(options.selectedDate);
    },
    [onSelection]
  );
  const {
    // @date_picker-react/hooks API
    activeMonths,
    goToPreviousMonths,
    goToNextMonths,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked: _isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus: _onDateFocus,
    onDateSelect,
    onDateHover,
    goToDate,
    isStartDate,
    isEndDate
  } = useDatePicker({
    picker,
    disabledDays,
    isDateBlocked,
    onSelection: _onSelection,
    onDateFocus
  });
  React10.useEffect(() => {
    if (isRangePicker && !selectedDate) {
      if (startDate && !endDate) {
        goToDate(startDate);
      }
      if (endDate && !startDate) {
        goToDate(endDate);
      }
      if (!endDate && !startDate) {
        goToDate(getToday());
      }
    }
  }, [startDate, endDate]);
  const activeMonth = activeMonths.length === 1 && activeMonths[0];
  const updateSelectedDate = React10.useCallback(
    (date) => {
      onDateSelect(date);
      _onDateFocus(date);
    },
    [onDateSelect, _onDateFocus]
  );
  const close = React10.useCallback((options) => {
    const autoFocusRef = options && options.autoFocusRef;
    setIsOpen(false);
    if (autoFocusRef && autoFocusRef.current && "focus" in autoFocusRef.current) {
      autoFocusRef.current.focus();
    }
  }, []);
  const open = React10.useCallback(
    (options) => {
      const autoFocusDate = options && options.autoFocusDate;
      setIsOpen(true);
      const selectAndFocusDate = autoFocusDate || (picker.focusedInput === "startDate" /* StartDate */ ? picker.startDate : picker.endDate);
      if (selectAndFocusDate) updateSelectedDate(selectAndFocusDate);
    },
    [picker, updateSelectedDate]
  );
  const forceUpdate = React10.useCallback(
    (options) => {
      const autoFocusDate = options && options.autoFocusDate;
      const selectAndFocusDate = autoFocusDate || (picker.focusedInput === "startDate" /* StartDate */ ? picker.startDate : picker.endDate);
      if (selectAndFocusDate) updateSelectedDate(selectAndFocusDate);
    },
    [picker, updateSelectedDate]
  );
  const toggle = React10.useCallback(() => {
    setIsOpen((v) => !v);
    const selectAndFocusDate = picker.focusedInput === "startDate" /* StartDate */ ? picker.startDate : picker.endDate;
    if (selectAndFocusDate) updateSelectedDate(selectAndFocusDate);
  }, [picker, updateSelectedDate]);
  const defaultValue2 = {
    datePickerId,
    triggerRef,
    closeOnSelection,
    close,
    open,
    toggle,
    withPreviousLabel,
    withNextLabel,
    monthLabelFormat,
    focusedDate,
    isDateFocused,
    isDateSelected,
    isDateHovered,
    isDateBlocked: _isDateBlocked,
    isFirstOrLastSelectedDate,
    onDateFocus: _onDateFocus,
    onDateSelect,
    onDateHover,
    goToPreviousMonths,
    goToNextMonths,
    isStartDate,
    isEndDate
  };
  const handleClickOutside = React10.useCallback(() => {
    if (closeOnBlur) close();
  }, [closeOnBlur, close]);
  const getTriggerProps = React10.useCallback(
    (options = {}) => {
      const { onClick, ...rest } = options;
      return {
        onClick: (evt) => {
          setIsOpen((v) => !v);
          const autoFocusDate = picker.focusedInput === "startDate" /* StartDate */ ? picker.startDate : picker.endDate;
          if (autoFocusDate) _onDateFocus(autoFocusDate);
          if (onClick) onClick(evt);
        },
        ref: triggerRef,
        ...rest
      };
    },
    [triggerRef, picker, _onDateFocus]
  );
  const handleKeyDown = React10.useCallback(
    (evt) => {
      if (NAVIGATION_KEYS.has(evt.key)) {
        evt.preventDefault();
        return;
      }
      if (isOpen && evt.key === ESC_KEY) {
        evt.preventDefault();
        evt.stopPropagation();
        close({ autoFocusRef: triggerRef });
      }
    },
    [close, isOpen, triggerRef]
  );
  const staticContainer = /* @__PURE__ */ React10.createElement(Context.Provider, { value: defaultValue2 }, /* @__PURE__ */ React10.createElement(
    "div",
    {
      className: classNames2("dig-DatePicker", datePicker, {
        "dig-DatePicker--shouldHideDropShadow": shouldHideDropShadow,
        [`${datePickerHiddenDropShadow}`]: shouldHideDropShadow
      }),
      onKeyDown: handleKeyDown,
      "data-testid": "digDatePicker"
    },
    activeMonth && /* @__PURE__ */ React10.createElement(
      Month,
      {
        key: `${activeMonth.year}-${activeMonth.month}`,
        year: activeMonth.year,
        month: activeMonth.month,
        firstDayOfWeek,
        format: monthLabelFormat,
        weekdayLabelFormat,
        dayLabelFormat
      }
    )
  ));
  const animationDirection = getAnimationDirection(placement);
  const animationStates = React10.useMemo(
    () => getDatePickerAnimation({ direction: animationDirection }),
    [animationDirection]
  );
  const { isMotionRunning } = useMotion({
    states: animationStates,
    currentState: isOpen ? "open" : "close",
    // if `typeof children !== 'function'`, we're rendering as a static container
    // and `motionRef` will never get set. This will force `useMotion` to simply no-op
    elementRef: motionRef
  });
  if (typeof children === "function") {
    const renderedChildren = children({
      getTriggerProps,
      triggerRef,
      close,
      open,
      toggle,
      forceUpdate
    });
    return /* @__PURE__ */ React10.createElement(
      ClickOutside,
      {
        className: `dig-DatePicker-backdrop ${datePickerBackDrop}`,
        isBlock,
        onClickOutside: handleClickOutside,
        shouldPropagateMouseEvents: shouldPropagateClickOutsideMouseEvents,
        isClickThroughPortaled: isPortaled,
        isActive: isOpen
      },
      renderedChildren,
      // The DatePicker's `isOpen` state is marked immediately, but it's still
      // visible until the motion state have transitioned to exited
      isOpen || isMotionRunning ? /* @__PURE__ */ React10.createElement(
        Overlay,
        {
          className: `pleskac-${animationDirection}`,
          auto,
          onChangePlacement,
          isPortaled,
          placement,
          positioningStrategy,
          ref: overlayRef,
          anchorRef: triggerRef,
          "data-testid": "digDatePickerOverlay"
        },
        /* @__PURE__ */ React10.createElement("div", { ref: motionRef }, staticContainer)
      ) : null
    );
  }
  return staticContainer;
};
DatePicker.displayName = "DatePicker";

// ../src/date_picker/date_picker_input.tsx
import * as React11 from "react";
import parse from "date-fns/parse";
import isValid from "date-fns/isValid";
import { TextInput } from "@dropbox/dig-components/text_fields";
import { UIIcon as UIIcon2 } from "@dropbox/dig-icons";
import { CalendarLine } from "@dropbox/dig-icons/assets";
var getDateString = (value, format2) => {
  if (!isValid(value)) {
    return "";
  }
  if (typeof format2 === "object") {
    return format2.dateToString(value);
  } else {
    return toDateString(value, format2 || "");
  }
};
var parseDateString = (value, format2) => {
  if (typeof format2 === "object") {
    return format2.stringToDate(value);
  } else {
    return parse(value, format2, /* @__PURE__ */ new Date());
  }
};
var DatePickerInput = ({
  id,
  value,
  onChange,
  onSelection,
  format: format2 = DATE_INPUT_FORMAT,
  // TextInput props
  disabled,
  inverse,
  isInvalid,
  isTransparent,
  size,
  "data-testid": dataTestId,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledBy,
  isDateBlocked,
  autocomplete,
  autoFocus,
  placeholder,
  // `rest` should be `DatePickerProps` since we've destructured `DatePickerInputProps` above
  ...rest
}) => {
  const [inputValue, setInputValue] = React11.useState(getDateString(value, format2));
  const syncInputValue = React11.useCallback(() => {
    let updatedDate = null;
    if (inputValue.length) {
      updatedDate = parseDateString(inputValue, format2);
      if (!isValid(updatedDate) || isDateBlocked && isDateBlocked(updatedDate)) {
        setInputValue(getDateString(value, format2));
        return value;
      }
    }
    if (onChange && !isSameDay(updatedDate, value)) {
      onChange(updatedDate);
    }
    return updatedDate;
  }, [inputValue, onChange, value, format2, isDateBlocked]);
  const createBlurHandler = (forceUpdate) => () => {
    const autoFocusDate = syncInputValue();
    forceUpdate({ autoFocusDate });
  };
  const handleInputOnChange = React11.useCallback((evt) => {
    setInputValue(evt.target.value);
  }, []);
  const handleSelection = React11.useCallback(
    (data) => {
      if (onChange && !isSameDay(data.selectedDate, value)) {
        onChange(data.selectedDate);
      }
      if (onSelection) onSelection(data);
    },
    [onChange, onSelection, value]
  );
  const createInputKeyDownHandler = React11.useCallback(
    (openFn) => (evt) => {
      switch (evt.key) {
        case SPACEBAR_KEY:
          evt.preventDefault();
          return;
        case ENTER_KEY:
          evt.preventDefault();
          const autoFocusDate = syncInputValue();
          openFn({ autoFocusDate });
          return;
      }
    },
    [syncInputValue]
  );
  const createInputKeyUpHandler = React11.useCallback(
    (openFn) => (evt) => {
      if (evt.key === SPACEBAR_KEY) {
        const autoFocusDate = syncInputValue();
        openFn({ autoFocusDate });
      }
    },
    [syncInputValue]
  );
  const createClickAccessoryHandler = React11.useCallback(
    (openFn) => () => {
      if (!disabled) {
        syncInputValue();
        openFn();
      }
    },
    [disabled, syncInputValue]
  );
  React11.useEffect(() => {
    setInputValue(value === null ? "" : getDateString(value, format2));
  }, [format2, value]);
  return /* @__PURE__ */ React11.createElement(
    DatePicker,
    {
      selectedDate: value,
      onSelection: handleSelection,
      isDateBlocked,
      ...rest
    },
    ({ getTriggerProps, open, forceUpdate }) => /* @__PURE__ */ React11.createElement(
      TextInput,
      {
        id,
        value: inputValue,
        disabled,
        inverse,
        isInvalid,
        isTransparent,
        size,
        "aria-label": ariaLabel,
        "aria-labelledby": ariaLabelledBy,
        "data-testid": dataTestId,
        ...getTriggerProps({
          onChange: handleInputOnChange,
          onKeyDown: createInputKeyDownHandler(open),
          onKeyUp: createInputKeyUpHandler(open),
          onBlur: createBlurHandler(forceUpdate)
        }),
        withLeftAccessory: /* @__PURE__ */ React11.createElement(UIIcon2, { src: CalendarLine, onClick: createClickAccessoryHandler(open) }),
        autoComplete: autocomplete,
        autoFocus,
        placeholder
      }
    )
  );
};

// ../src/date_picker/date_picker_range.tsx
import * as React12 from "react";
var Start = () => {
  const ctx = React12.useContext(RangeContext);
  if (!ctx) {
    console.warn("DatePickerRange.Start should only be used within a DatePickerRange.");
    return null;
  }
  return /* @__PURE__ */ React12.createElement(DatePicker, { ...ctx.getStartProps() });
};
Start.displayName = "Start";
var StartInput = (props) => {
  const ctx = React12.useContext(RangeContext);
  if (!ctx) {
    console.warn("DatePickerRange.StartInput should only be used within a DatePickerRange.");
    return null;
  }
  return /* @__PURE__ */ React12.createElement(DatePickerInput, { ...props, ...ctx.getStartProps() });
};
StartInput.displayName = "StartInput";
var End = () => {
  const ctx = React12.useContext(RangeContext);
  if (!ctx) {
    console.warn("DatePickerRange.End should only be used within a DatePickerRange.");
    return null;
  }
  return /* @__PURE__ */ React12.createElement(DatePicker, { ...ctx.getEndProps() });
};
End.displayName = "End";
var EndInput = (props) => {
  const ctx = React12.useContext(RangeContext);
  if (!ctx) {
    console.warn("DatePickerRange.EndInput should only be used within a DatePickerRange.");
    return null;
  }
  return /* @__PURE__ */ React12.createElement(DatePickerInput, { ...props, ...ctx.getEndProps() });
};
EndInput.displayName = "EndInput";
var DatePickerRange = ({
  selectedRange,
  onSelection,
  withPreviousLabel = STRINGS.previous,
  withNextLabel = STRINGS.next,
  auto = true,
  placement = "bottom-start",
  positioningStrategy = "absolute",
  closeOnBlur = true,
  closeOnSelection = true,
  shouldPropagateClickOutsideMouseEvents = false,
  isDateBlocked,
  isBlock = false,
  isPortaled = true,
  children
}) => {
  const handleSelectionStart = React12.useCallback(
    (data) => {
      if (onSelection) {
        onSelection({
          startDate: data.startDate,
          endDate: data.startDate && selectedRange[1] && data.startDate > selectedRange[1] ? data.startDate : selectedRange[1],
          selectedDate: data.startDate
        });
      }
    },
    [selectedRange, onSelection]
  );
  const handleSelectionEnd = React12.useCallback(
    (data) => {
      if (onSelection) {
        onSelection({
          startDate: data.endDate && selectedRange[0] && data.endDate < selectedRange[0] ? data.endDate : selectedRange[0],
          endDate: data.endDate,
          selectedDate: data.endDate
        });
      }
    },
    [selectedRange, onSelection]
  );
  const sharedProps = React12.useMemo(
    () => ({
      withPreviousLabel,
      withNextLabel,
      auto,
      placement,
      positioningStrategy,
      closeOnBlur,
      closeOnSelection,
      shouldPropagateClickOutsideMouseEvents,
      isBlock,
      isPortaled,
      isDateBlocked
    }),
    [
      withPreviousLabel,
      withNextLabel,
      auto,
      placement,
      positioningStrategy,
      closeOnBlur,
      closeOnSelection,
      shouldPropagateClickOutsideMouseEvents,
      isBlock,
      isPortaled,
      isDateBlocked
    ]
  );
  const getStartProps = React12.useCallback(
    () => ({
      ...sharedProps,
      focusedInput: "startDate" /* StartDate */,
      onSelection: handleSelectionStart,
      selectedDate: selectedRange[0]
    }),
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectionStart, sharedProps]
  );
  const getEndProps = React12.useCallback(
    () => ({
      ...sharedProps,
      focusedInput: "endDate" /* EndDate */,
      onSelection: handleSelectionEnd,
      selectedDate: selectedRange[1]
    }),
    // eslint-disable-next-line react-compiler/react-compiler
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [handleSelectionEnd, sharedProps]
  );
  const ctx = React12.useMemo(
    () => ({ selectedRange, getStartProps, getEndProps }),
    [selectedRange, getStartProps, getEndProps]
  );
  return /* @__PURE__ */ React12.createElement(RangeContext.Provider, { value: ctx }, children);
};
DatePickerRange.displayName = "DatePickerRange";
DatePickerRange.Start = Start;
DatePickerRange.StartInput = StartInput;
DatePickerRange.End = End;
DatePickerRange.EndInput = EndInput;
export {
  DatePicker,
  DatePickerInput,
  DatePickerRange
};
